import { WebDashboardContentSpecSlicersInner } from "@coeff/agent-types";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { DashboardWindow } from "./dashboardHelpers";
import { DateRangeSelection } from "./dateRangeSelection";
import { DateRangeSlicer } from "./DateRangeSlicer";
import { MobileBottomSheet } from "./MobileBottomSheet";
import { MultiSelectSlicer } from "./MultiSelectSlicer";
import { SingleSelectSlicer } from "./SingleSelectSlicer";
import { hasSavableDefaultChanges } from "./slicerDefaults";
import { SlicerErrorBoundary } from "./SlicerErrorBoundary";
import { SlicerErrorCard } from "./SlicerErrorCard";
import { DateRange, SelectOption, SlicerState, SlicerValue } from "./useDashboardRuntime";
import { useIsMobile } from "./useIsMobile";

const PersonalizedViewEditorSlicer: React.FC<{
  label: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  viewerEmail?: string | null;
}> = ({ label, options, value, onChange, viewerEmail }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showTooltip, setShowTooltip] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const ownerDoc = containerRef.current?.ownerDocument ?? document;
    const onOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch("");
        setHighlightedIndex(-1);
      }
    };
    ownerDoc.addEventListener("mousedown", onOutsideClick);
    return () => ownerDoc.removeEventListener("mousedown", onOutsideClick);
  }, [isOpen]);

  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  // When search has no match, the "Use ..." entry acts as an extra option at the end.
  const hasCustomEntry = filtered.length === 0 && search.trim() !== "";
  const totalItems = filtered.length + (hasCustomEntry ? 1 : 0);

  // If the current value is the editor's own email but they have no data rows,
  // fall back to the first available option automatically.
  useEffect(() => {
    if (!viewerEmail || value !== viewerEmail || options.length === 0) {
      return;
    }
    const emailInOptions = options.some(o => o.value === viewerEmail);
    if (!emailInOptions) {
      onChange(options[0].value);
    }
  }, [options, value, viewerEmail, onChange]);

  // Reset highlight when results change.
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  // Scroll highlighted item into view.
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) {
      return;
    }
    const item = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const commit = (val: string) => {
    if (!val) {
      return;
    }
    onChange(val);
    setIsOpen(false);
    setSearch("");
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(i => (i + 1) % totalItems);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(i => (i - 1 + totalItems) % totalItems);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        commit(filtered[highlightedIndex].value);
      } else if (highlightedIndex === filtered.length && hasCustomEntry) {
        commit(search.trim());
      } else {
        // No highlight — fall back to first match or free-text.
        commit(filtered.length > 0 ? filtered[0].value : search.trim());
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearch("");
      setHighlightedIndex(-1);
    }
  };

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setIsOpen(v => !v)}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-themed border border-card-border bg-card-background shadow-card hover:bg-card-text-button-hover-background transition-colors"
      >
        <div
          className="relative inline-flex"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onClick={e => e.stopPropagation()}
        >
          <span className="material-icons !text-base text-card-foreground/60 cursor-default">
            lock
          </span>
          {showTooltip && (
            <div className="absolute bottom-full left-0 mb-2 w-64 bg-card-background border border-card-border text-card-foreground text-sm rounded-themed px-3 py-2 shadow-card z-50 whitespace-normal pointer-events-none">
              Use View as to verify Personalized Views.
            </div>
          )}
        </div>
        <span className="text-sm font-semibold text-card-foreground">
          View as: {value === viewerEmail ? "Me" : value || "—"}
        </span>
        <span className="material-icons !text-base text-card-foreground/60">expand_more</span>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 min-w-[240px] bg-card-background border border-card-border rounded-themed shadow-card z-50 overflow-hidden">
          <div className="p-2 border-b border-card-border">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search..."
              className="w-full px-2 py-1.5 text-sm bg-card-background border border-card-border rounded-themed outline-none text-card-foreground placeholder:text-card-foreground/50"
            />
          </div>
          <div ref={listRef} className="max-h-48 overflow-y-auto">
            {filtered.map((o, i) => (
              <button
                key={o.value}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm text-card-foreground ${
                  i === highlightedIndex
                    ? "bg-card-text-button-hover-background"
                    : "hover:bg-card-text-button-hover-background"
                } ${value === o.value ? "font-semibold" : ""}`}
                onClick={() => commit(o.value)}
              >
                {o.value === viewerEmail ? "Me" : o.label}
              </button>
            ))}
            {hasCustomEntry && (
              <button
                type="button"
                className={`w-full text-left px-3 py-2 text-sm text-card-foreground ${
                  highlightedIndex === filtered.length
                    ? "bg-card-text-button-hover-background"
                    : "hover:bg-card-text-button-hover-background"
                }`}
                onClick={() => commit(search.trim())}
              >
                Use &ldquo;{search.trim()}&rdquo;
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export interface SlicersSectionProps {
  slicers: WebDashboardContentSpecSlicersInner[];
  slicerState: SlicerState;
  defaultSlicerState: SlicerState;
  allTimeDateRanges: Record<string, DateRange>;
  resolvedOptions: Record<string, SelectOption[]>;
  setSlicerValue: (name: string, value: SlicerValue, selection?: DateRangeSelection | null) => void;
  resetSlicers: () => void;
  isSlicersActive: boolean;
  targetWindow: DashboardWindow;
  idSuffix: string;
  /** Map of slicer name -> error message for slicers that failed to initialize. */
  slicerInitErrors?: Record<string, string>;
  /** When true, slicers are shown but non-interactive (runtime not yet initialized) */
  isDisabled?: boolean;
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  /** Email of the currently logged-in viewer (null for anonymous/public dashboards) */
  viewerEmail?: string | null;
  canEdit?: boolean;
  /**
   * Editor-only: persist the current filter state as what the dashboard opens on. Omit for
   * viewers — the affordance is not rendered at all then. Rejects on failure so the button can
   * report it rather than silently claiming success.
   */
  onSaveAsDefault?: () => Promise<void>;
}

export const SlicersSection: React.FC<SlicersSectionProps> = ({
  slicers,
  slicerState,
  defaultSlicerState,
  allTimeDateRanges,
  resolvedOptions,
  setSlicerValue,
  resetSlicers,
  isSlicersActive,
  targetWindow,
  idSuffix,
  slicerInitErrors,
  isDisabled = false,
  onFixBlock,
  viewerEmail = null,
  canEdit = false,
  onSaveAsDefault,
}) => {
  const generateId = (name: string, prefix = "") => {
    return `${prefix}${name}${idSuffix ? `_${idSuffix}` : ""}`;
  };

  const setSlicerValueRef = useRef(setSlicerValue);
  const slicerChangeHandlersRef = useRef<
    Record<string, (value: SlicerValue, selection?: DateRangeSelection | null) => void>
  >({});

  useEffect(() => {
    setSlicerValueRef.current = setSlicerValue;
  }, [setSlicerValue]);

  useEffect(() => {
    const activeSlicerNames = new Set(slicers.map(slicer => slicer.name));
    Object.keys(slicerChangeHandlersRef.current).forEach(name => {
      if (!activeSlicerNames.has(name)) {
        delete slicerChangeHandlersRef.current[name];
      }
    });
  }, [slicers]);

  const getSlicerChangeHandler = useCallback((slicerName: string) => {
    const existingHandler = slicerChangeHandlersRef.current[slicerName];
    if (existingHandler) {
      return existingHandler;
    }

    const newHandler = (value: SlicerValue, selection?: DateRangeSelection | null) => {
      setSlicerValueRef.current(slicerName, value, selection);
    };
    slicerChangeHandlersRef.current[slicerName] = newHandler;
    return newHandler;
  }, []);

  // Below 640px the inline filter row collapses into a "Filters" trigger that opens a bottom
  // sheet (matches the mobile Figma). The slicers render in exactly one place per viewport
  // (inline OR in the sheet) to avoid duplicating their DOM ids.
  const isMobile = useIsMobile(targetWindow);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // "saving" | "saved" drive the button's own label. The save is silent (no dialog, no toast on
  // success), but it still has to say something: a server write that quietly did nothing looks
  // identical to one that worked. "saved" clears itself after a beat.
  const [saveDefaultStatus, setSaveDefaultStatus] = useState<"idle" | "saving" | "saved">("idle");
  useEffect(() => {
    if (saveDefaultStatus !== "saved") {
      return;
    }
    const timer = targetWindow.setTimeout(() => setSaveDefaultStatus("idle"), 2000);
    return () => targetWindow.clearTimeout(timer);
  }, [saveDefaultStatus, targetWindow]);

  const hasSavableChanges = hasSavableDefaultChanges(slicers, slicerState, defaultSlicerState);

  const handleSaveAsDefault = async () => {
    if (!onSaveAsDefault || saveDefaultStatus === "saving") {
      return;
    }
    setSaveDefaultStatus("saving");
    try {
      await onSaveAsDefault();
      setSaveDefaultStatus("saved");
    } catch {
      // The caller owns the error toast; just release the button.
      setSaveDefaultStatus("idle");
    }
  };

  const visibleSlicerCount = canEdit
    ? slicers.length
    : slicers.filter(s => s.type !== "personalized_view").length;

  if (visibleSlicerCount === 0) {
    return null;
  }

  const renderSlicerSkeleton = (slicer: WebDashboardContentSpecSlicersInner) => (
    <div
      key={slicer.name}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-main-foreground/10 bg-main-foreground/5"
    >
      <span className="text-sm text-main-muted-foreground opacity-60">{slicer.label}</span>
      <div className="animate-pulse rounded bg-main-foreground/10 h-4 w-16" />
    </div>
  );

  /**
   * Renders one slicer's control.
   *
   * `opts.idSuffixExtra` is appended to the DOM id, and `opts.dateMobileVariant` picks the date
   * filter's mobile presentation. Both exist because on phones the date filter renders twice — as a
   * pill on the dashboard filter row and as a row inside the Filters sheet — and two elements may
   * not share a DOM id. The pill keeps the canonical id (it is the always-mounted one); the sheet
   * copy is suffixed.
   */
  const renderSlicerControl = (
    slicer: WebDashboardContentSpecSlicersInner,
    opts: { idSuffixExtra?: string; dateMobileVariant?: "pill" | "row" } = {}
  ) => {
    const controlId = `${generateId(slicer.name, "slicer_")}${opts.idSuffixExtra ?? ""}`;
    const key = `${slicer.name}${opts.idSuffixExtra ?? ""}`;

    const initError = slicerInitErrors?.[slicer.name];
    if (initError !== undefined) {
      const errorMessage =
        initError || `Filter "${slicer.label ?? slicer.name}" failed to initialize`;
      return (
        <SlicerErrorCard
          key={key}
          label={slicer.label ?? slicer.name}
          slicerName={slicer.name}
          errorMessage={errorMessage}
          onFixBlock={onFixBlock}
        />
      );
    }
    if (slicer.type === "single_select") {
      return (
        <SlicerErrorBoundary key={key} slicerName={slicer.name} onFixBlock={onFixBlock}>
          <SingleSelectSlicer
            id={controlId}
            name={slicer.name}
            label={slicer.label}
            options={resolvedOptions[slicer.name] ?? slicer.options ?? []}
            value={(slicerState[slicer.name] as string) || ""}
            onChange={getSlicerChangeHandler(slicer.name)}
            targetWindow={targetWindow}
          />
        </SlicerErrorBoundary>
      );
    } else if (slicer.type === "multi_select") {
      return (
        <SlicerErrorBoundary key={key} slicerName={slicer.name} onFixBlock={onFixBlock}>
          <MultiSelectSlicer
            id={controlId}
            name={slicer.name}
            label={slicer.label}
            options={resolvedOptions[slicer.name] ?? []}
            value={(slicerState[slicer.name] as string[]) || []}
            onChange={getSlicerChangeHandler(slicer.name)}
            targetWindow={targetWindow}
          />
        </SlicerErrorBoundary>
      );
    } else if (slicer.type === "date_range") {
      return (
        <SlicerErrorBoundary key={key} slicerName={slicer.name} onFixBlock={onFixBlock}>
          <DateRangeSlicer
            id={controlId}
            name={slicer.name}
            label={slicer.label}
            granularity={slicer.granularity}
            defaultDateRange={defaultSlicerState[slicer.name] as DateRange}
            defaultSelection={slicer.default_selection}
            allTimeDateRange={allTimeDateRanges[slicer.name]}
            initialDateRange={slicerState[slicer.name] as DateRange}
            onChange={getSlicerChangeHandler(slicer.name)}
            targetWindow={targetWindow}
            mobileVariant={opts.dateMobileVariant}
          />
        </SlicerErrorBoundary>
      );
    } else if (slicer.type === "personalized_view") {
      if (!canEdit) {
        return null;
      }
      const currentValue = (slicerState[slicer.name] as string) || viewerEmail || "";
      return (
        <SlicerErrorBoundary key={key} slicerName={slicer.name} onFixBlock={onFixBlock}>
          <PersonalizedViewEditorSlicer
            label={slicer.label}
            options={resolvedOptions[slicer.name] ?? []}
            value={currentValue}
            onChange={getSlicerChangeHandler(slicer.name)}
            viewerEmail={viewerEmail}
          />
        </SlicerErrorBoundary>
      );
    }
    return null;
  };

  const slicerControls = isDisabled
    ? slicers
        .filter(s => !(s.type === "personalized_view" && !canEdit))
        .map(slicer => renderSlicerSkeleton(slicer))
    : slicers.map(slicer => renderSlicerControl(slicer));

  // Mobile (<640px): the dashboard filter row is the date filter as a pill plus a single "All
  // Filters" pill for everything else, which opens the bottom sheet (Figma "Date + More Filters").
  // Date filters stay reachable in one tap because they are the most-used control; every other
  // filter type collapses behind the one button so the row never wraps or scrolls.
  if (isMobile) {
    const dateSlicers = slicers.filter(s => s.type === "date_range");
    // Anything that is not a date filter and is actually shown to this viewer. personalized_view is
    // editor-only, so for a non-editor it must neither make the filters chip appear nor be counted
    // in its label.
    const collapsedSlicers = slicers.filter(
      s => s.type !== "date_range" && !(s.type === "personalized_view" && !canEdit)
    );
    // How many filters are currently set to something other than their default.
    //
    // Scoped to collapsedSlicers, which excludes the date range. Note the sheet itself DOES render
    // the date range (renderSlicerControl is called with dateMobileVariant "row" below), so this is
    // not "count what the sheet contains" — it is deliberately narrower. The date range already has
    // its own pill on this row displaying its current value, so folding it into the chip's number
    // would double-represent one filter: the viewer would see the range in the pill and an unexplained
    // +1 beside it. personalized_view is excluded for the same class of reason the desktop row excludes
    // it — it is a "view as" tool, not a data filter — even for editors, who are the only ones who see it.
    //
    // Deliberately not gated on the isSlicersActive prop, and with no Math.max(1, …) floor. The
    // pre-Figma version of this counter had both, and both were right at the time: the count then
    // covered every slicer, so isSlicersActive being true implied at least one counted filter
    // differed from its default. Excluding the date range breaks that implication — isSlicersActive
    // is true whenever the range alone is changed, and a floor of 1 would then show a phantom
    // "1 Filter" with no non-date filter touched. Comparing the collapsed set directly holds.
    const activeFilterCount = collapsedSlicers.filter(
      s =>
        s.type !== "personalized_view" &&
        JSON.stringify(slicerState[s.name]) !== JSON.stringify(defaultSlicerState[s.name])
    ).length;
    return (
      <div
        id={generateId("slicersContainer")}
        className={`mb-2 transition-opacity duration-300 ${
          isDisabled ? "opacity-40 pointer-events-none" : ""
        }`}
      >
        {/* min-w-0 on the row + truncate inside the date pill: a long range shortens the pill
            rather than pushing the filters chip off-screen or wrapping to a second line. */}
        <div className="flex min-w-0 items-center gap-[12px]">
          {isDisabled
            ? slicerControls
            : dateSlicers.map(slicer => renderSlicerControl(slicer, { dateMobileVariant: "pill" }))}

          {!isDisabled && collapsedSlicers.length > 0 && (
            <button
              type="button"
              onClick={() => setFilterSheetOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={filterSheetOpen}
              className="flex h-[32px] shrink-0 items-center gap-[8px] rounded-[8px] bg-card-background px-[12px] text-card-foreground shadow-card cursor-pointer"
            >
              <span className="material-icons !text-[20px]">filter_list</span>
              {/* Falls back to the bare word "Filters" at zero rather than "0 Filters", which is what
                  this counter did before the Figma pass replaced it with a fixed "All Filters". */}
              <span className="text-[12px] leading-[18px] font-semibold">
                {activeFilterCount > 0
                  ? `${activeFilterCount} Filter${activeFilterCount === 1 ? "" : "s"}`
                  : "Filters"}
              </span>
            </button>
          )}
        </div>

        {filterSheetOpen && (
          <MobileBottomSheet
            onClose={() => setFilterSheetOpen(false)}
            targetDocument={targetWindow.document}
            ariaLabel="Filters"
            className="bg-main-background flex flex-col"
            desktopClassName=""
            // Fixed height rather than the sheet's default content-driven `max-h-[85vh]`: the Figma
            // draws the same sheet box whether the filter sections are collapsed or expanded, so
            // expanding one must scroll the body, not resize (and re-animate) the whole sheet.
            // 85vh matches the top edge the design shows on a 390x844 phone.
            mobileClassName="h-[85vh]"
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="material-icons !text-lg text-main-foreground">filter_list</span>
                <h2 className="text-base font-semibold text-main-foreground">Filters</h2>
              </div>
              <button
                type="button"
                onClick={() => setFilterSheetOpen(false)}
                aria-label="Close"
                className="p-1.5 text-main-muted-foreground hover:text-main-foreground rounded-full hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
              >
                <span className="material-icons !text-xl">close</span>
              </button>
            </div>
            {/* Slicers stacked full width: [&>*] stretches each slicer's root, which for the
                inline-flex ones (personalized view, error card) is what makes them fill the sheet.
                Width only — display is left as-is. The three filter slicers all draw their own
                full-width card on mobile, so the .themed-slicer-input overrides that used to live
                here (for SingleSelectSlicer's bare <select>) no longer match anything.
                `flex-1 min-h-0` makes this the one scrolling region inside the fixed-height sheet,
                so the header and the Reset/Done footer stay pinned. gap-[8px] is the Figma's gap
                between filter cards. */}
            <div className="flex flex-1 min-h-0 flex-col gap-[8px] pt-[10px] px-5 pb-4 overflow-y-auto overscroll-y-none [&>*]:w-full">
              {/* The sheet lists every filter, date included, so it stays a complete view of the
                  dashboard's filter state. Only the date filter needs an id suffix — it is the one
                  type also rendered as a pill on the row behind the sheet. */}
              {slicers.map(slicer =>
                renderSlicerControl(slicer, {
                  idSuffixExtra: slicer.type === "date_range" ? "_sheet" : undefined,
                  dateMobileVariant: "row",
                })
              )}
            </div>
            <div className="flex gap-3 px-5 py-4 flex-shrink-0">
              <button
                type="button"
                onClick={resetSlicers}
                disabled={!isSlicersActive}
                className="flex-[2] h-12 rounded-xl bg-primary-button-background/10 text-primary-button-background text-sm font-semibold hover:bg-primary-button-background/15 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset Filters
              </button>
              <button
                type="button"
                onClick={() => setFilterSheetOpen(false)}
                className="flex-[3] h-12 rounded-xl bg-primary-button-background text-primary-button-foreground text-sm font-semibold hover:bg-primary-button-hover-background hover:text-primary-button-hover-foreground transition-colors cursor-pointer"
              >
                Done
              </button>
            </div>
          </MobileBottomSheet>
        )}
      </div>
    );
  }

  return (
    <div
      id={generateId("slicersContainer")}
      className={`mb-2 transition-opacity duration-300 ${
        isDisabled ? "opacity-40 pointer-events-none" : ""
      }`}
    >
      <div className="flex gap-3 flex-wrap items-center">
        <div className="flex items-center gap-1.5 text-main-foreground">
          <span className="material-icons !text-lg">filter_list</span>
          <span className="text-sm font-medium">
            {visibleSlicerCount === 1 ? "Filter" : "Filters"}
          </span>
        </div>
        {slicerControls}

        {/* Plain text buttons, not pills (design feedback: on the card-surface pill treatment
            these read as two more filters). Both in main-foreground — the theme's own text
            color, near-black on light themes and near-white on dark ones — so they read the
            same on every theme instead of riding a per-theme accent. */}
        {!isDisabled && (
          <button
            id={generateId("resetFiltersBtn")}
            onClick={resetSlicers}
            className={`px-2 py-1 rounded-themed text-sm font-medium text-main-foreground hover:bg-main-text-button-hover-background transition-colors cursor-pointer ${
              isSlicersActive ? "" : "invisible"
            }`}
          >
            Reset
          </button>
        )}

        {/* Editors only, and only while a savable filter differs from the default — so it
            appears exactly when there is something to save and goes away once saved.
            hasSavableDefaultChanges narrows isSlicersActive: the latter is also true when
            only the "view as" preview changed, which is not part of the default and would offer
            a save that stores nothing. isSlicersActive is ALSO required — not for its meaning,
            but for its timing: it updates inside the same view transition that reveals Reset,
            so both buttons appear in the same frame instead of Save popping in while the
            blocks are still re-rendering. Same plain-text treatment as Reset. */}
        {!isDisabled && onSaveAsDefault && isSlicersActive && hasSavableChanges && (
          <button
            id={generateId("saveFiltersAsDefaultBtn")}
            onClick={handleSaveAsDefault}
            disabled={saveDefaultStatus === "saving"}
            className="px-2 py-1 rounded-themed text-sm font-medium text-main-foreground hover:bg-main-text-button-hover-background transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-default"
          >
            {saveDefaultStatus === "saving"
              ? "Saving…"
              : saveDefaultStatus === "saved"
              ? "Saved"
              : "Save"}
          </button>
        )}
      </div>
    </div>
  );
};
